import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SubscriptionProvider } from "@/hooks/useSubscription";
import Landing from "./pages/Landing";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Lazy load all pages except Landing for better initial bundle size
const MainLayout = lazy(() => import("./components/Layout/MainLayout").then(m => ({ default: m.MainLayout })));
const FeatureGate = lazy(() => import("./components/FeatureGate").then(m => ({ default: m.FeatureGate })));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Clients = lazy(() => import("./pages/Clients"));
const Jobs = lazy(() => import("./pages/Jobs"));
const Estimates = lazy(() => import("./pages/Estimates"));
const Proposals = lazy(() => import("./pages/Proposals"));
const Contracts = lazy(() => import("./pages/Contracts"));
const Invoices = lazy(() => import("./pages/Invoices"));
const Expenses = lazy(() => import("./pages/Expenses"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(() => import("./pages/Settings"));
const Vendors = lazy(() => import("./pages/Vendors"));
const Auth = lazy(() => import("./pages/Auth"));
const Surveys = lazy(() => import("./pages/Surveys"));
const PublicSurvey = lazy(() => import("./pages/PublicSurvey"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PayInvoice = lazy(() => import("./pages/PayInvoice"));
const RecurringBilling = lazy(() => import("./pages/RecurringBilling"));
const Profitability = lazy(() => import("./pages/Profitability"));
const Team = lazy(() => import("./pages/Team"));
const SavedItems = lazy(() => import("./pages/SavedItems"));
const Organizations = lazy(() => import("./pages/Organizations"));
const Funnels = lazy(() => import("./pages/Funnels"));
const SidebarCustomization = lazy(() => import("./pages/SidebarCustomization"));
const ActivityLogs = lazy(() => import("./pages/ActivityLogs"));
const Permissions = lazy(() => import("./pages/Permissions"));
const AdvancedReports = lazy(() => import("./pages/AdvancedReports"));
const AIConsultant = lazy(() => import("./pages/AIConsultant"));
const Employees = lazy(() => import("./pages/Employees"));
const Schedule = lazy(() => import("./pages/Schedule"));
const Payroll = lazy(() => import("./pages/Payroll"));
const ScheduleDemo = lazy(() => import("./pages/ScheduleDemo"));
const FreeTrial = lazy(() => import("./pages/FreeTrial"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const EmployeePortal = lazy(() => import("./pages/EmployeePortal"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Documents = lazy(() => import("./pages/Documents"));
const Messages = lazy(() => import("./pages/Messages"));
const Accounting = lazy(() => import("./pages/Accounting"));
const Assets = lazy(() => import("./pages/Assets"));
const ProjectManagement = lazy(() => import("./pages/ProjectManagement"));
const Benefits = lazy(() => import("./pages/Benefits"));
const Budgeting = lazy(() => import("./pages/Budgeting"));
const SupportTickets = lazy(() => import("./pages/SupportTickets"));
const EmailMarketing = lazy(() => import("./pages/EmailMarketing"));
const KnowledgeBase = lazy(() => import("./pages/KnowledgeBase"));
const Integrations = lazy(() => import("./pages/Integrations"));
const APIManagement = lazy(() => import("./pages/APIManagement"));
const Appointments = lazy(() => import("./pages/Appointments"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const CustomFields = lazy(() => import("./pages/CustomFields"));
const LeadForms = lazy(() => import("./pages/LeadForms"));
const WorkOrders = lazy(() => import("./pages/WorkOrders"));
const PTOManagement = lazy(() => import("./pages/PTOManagement"));
const PerformanceReviews = lazy(() => import("./pages/PerformanceReviews"));
const FollowUpSequences = lazy(() => import("./pages/FollowUpSequences"));
const ChartOfAccounts = lazy(() => import("./pages/ChartOfAccounts"));
const JournalEntries = lazy(() => import("./pages/JournalEntries"));
const BankReconciliation = lazy(() => import("./pages/BankReconciliation"));
const TaxReports = lazy(() => import("./pages/TaxReports"));
const FinancialReports = lazy(() => import("./pages/FinancialReports"));
const CalendarPage = lazy(() => import("./pages/Calendar"));
const ImportClients = lazy(() => import("./pages/ImportClients"));
const ImportCenter = lazy(() => import("./pages/ImportCenter"));
const Prospects = lazy(() => import("./pages/Prospects"));
const DoorTracker = lazy(() => import("./pages/DoorTracker"));
const Autopilot = lazy(() => import("./pages/Autopilot"));
const ARAging = lazy(() => import("./pages/ARAging"));
const MileageTracker = lazy(() => import("./pages/MileageTracker"));
const Form1099 = lazy(() => import("./pages/Form1099"));
const CashFlowForecast = lazy(() => import("./pages/CashFlowForecast"));
const CRM = lazy(() => import("./pages/CRM"));
const AccountingHub = lazy(() => import("./pages/AccountingHub"));
const MigrationWizard = lazy(() => import("./pages/MigrationWizard"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Features = lazy(() => import("./pages/Features"));
const About = lazy(() => import("./pages/About"));
const SwitchAndSave = lazy(() => import("./pages/SwitchAndSave"));
const MigrationConcierge = lazy(() => import("./pages/MigrationConcierge"));
const VoiceAI = lazy(() => import("./pages/VoiceAI"));
const Security = lazy(() => import("./pages/Security"));
const Contact = lazy(() => import("./pages/Contact"));
const Status = lazy(() => import("./pages/Status"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const LaunchReadiness = lazy(() => import("./pages/LaunchReadiness"));
const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Tracks page views on every route change
import { usePageTracking } from "@/hooks/usePageTracking";
const PageTracker = () => {
  usePageTracking();
  return null;
};

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ThemeProvider>
          <SubscriptionProvider>
          <Suspense fallback={<PageLoader />}>
            <PageTracker />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/schedule-demo" element={<ScheduleDemo />} />
              <Route path="/free-trial" element={<FreeTrial />} />
              <Route path="/dashboard" element={<MainLayout><Dashboard /></MainLayout>} />
              <Route path="/calendar" element={<MainLayout><CalendarPage /></MainLayout>} />
              <Route path="/clients" element={<MainLayout><Clients /></MainLayout>} />
              <Route path="/import-clients" element={<MainLayout><ImportClients /></MainLayout>} />
              <Route path="/vendors" element={<MainLayout><Vendors /></MainLayout>} />
              <Route path="/jobs" element={<MainLayout><Jobs /></MainLayout>} />
              <Route path="/saved-items" element={<MainLayout><SavedItems /></MainLayout>} />
              <Route path="/estimates" element={<MainLayout><Estimates /></MainLayout>} />
              <Route path="/proposals" element={<MainLayout><Proposals /></MainLayout>} />
              <Route path="/contracts" element={<MainLayout><Contracts /></MainLayout>} />
              <Route path="/invoices" element={<MainLayout><Invoices /></MainLayout>} />
              <Route path="/expenses" element={<MainLayout><Expenses /></MainLayout>} />
              <Route path="/reports" element={<MainLayout><Reports /></MainLayout>} />
              <Route path="/profitability" element={<MainLayout><Profitability /></MainLayout>} />
              <Route path="/team" element={<MainLayout><Team /></MainLayout>} />
              <Route path="/organizations" element={<MainLayout><Organizations /></MainLayout>} />
              <Route path="/surveys" element={<MainLayout><Surveys /></MainLayout>} />
              <Route path="/funnels" element={<MainLayout><Funnels /></MainLayout>} />
              <Route path="/survey/:surveyId" element={<PublicSurvey />} />
              <Route path="/sidebar-customization" element={<MainLayout><SidebarCustomization /></MainLayout>} />
              <Route path="/activity-logs" element={<MainLayout><ActivityLogs /></MainLayout>} />
              <Route path="/permissions" element={<MainLayout><Permissions /></MainLayout>} />
              <Route path="/advanced-reports" element={<MainLayout><FeatureGate requiredTier="professional"><AdvancedReports /></FeatureGate></MainLayout>} />
              <Route path="/ai-consultant" element={<MainLayout><FeatureGate requiredTier="professional"><AIConsultant /></FeatureGate></MainLayout>} />
              <Route path="/employees" element={<MainLayout><Employees /></MainLayout>} />
              <Route path="/schedule" element={<MainLayout><Schedule /></MainLayout>} />
              <Route path="/payroll" element={<MainLayout><Payroll /></MainLayout>} />
              <Route path="/settings" element={<MainLayout><Settings /></MainLayout>} />
              <Route path="/pay-invoice/:invoiceId" element={<PayInvoice />} />
              <Route path="/client-portal" element={<ClientPortal />} />
              <Route path="/employee-portal" element={<EmployeePortal />} />
              <Route path="/recurring-billing" element={<MainLayout><RecurringBilling /></MainLayout>} />
              <Route path="/inventory" element={<MainLayout><Inventory /></MainLayout>} />
              <Route path="/documents" element={<MainLayout><Documents /></MainLayout>} />
              <Route path="/messages" element={<MainLayout><Messages /></MainLayout>} />
              <Route path="/accounting" element={<MainLayout><Accounting /></MainLayout>} />
              <Route path="/assets" element={<MainLayout><Assets /></MainLayout>} />
              <Route path="/project-management" element={<MainLayout><ProjectManagement /></MainLayout>} />
              <Route path="/benefits" element={<MainLayout><Benefits /></MainLayout>} />
              <Route path="/budgeting" element={<MainLayout><Budgeting /></MainLayout>} />
              <Route path="/support-tickets" element={<MainLayout><SupportTickets /></MainLayout>} />
              <Route path="/email-marketing" element={<MainLayout><EmailMarketing /></MainLayout>} />
              <Route path="/knowledge-base" element={<MainLayout><KnowledgeBase /></MainLayout>} />
              <Route path="/integrations" element={<MainLayout><Integrations /></MainLayout>} />
              <Route path="/api-management" element={<MainLayout><FeatureGate requiredTier="business"><APIManagement /></FeatureGate></MainLayout>} />
              <Route path="/appointments" element={<MainLayout><Appointments /></MainLayout>} />
              <Route path="/custom-fields" element={<MainLayout><CustomFields /></MainLayout>} />
              <Route path="/lead-forms" element={<MainLayout><LeadForms /></MainLayout>} />
              <Route path="/work-orders" element={<MainLayout><WorkOrders /></MainLayout>} />
              <Route path="/pto-management" element={<MainLayout><PTOManagement /></MainLayout>} />
              <Route path="/performance-reviews" element={<MainLayout><PerformanceReviews /></MainLayout>} />
              <Route path="/follow-up-sequences" element={<MainLayout><FollowUpSequences /></MainLayout>} />
              <Route path="/chart-of-accounts" element={<MainLayout><ChartOfAccounts /></MainLayout>} />
              <Route path="/journal-entries" element={<MainLayout><JournalEntries /></MainLayout>} />
              <Route path="/bank-reconciliation" element={<MainLayout><BankReconciliation /></MainLayout>} />
              <Route path="/tax-reports" element={<MainLayout><TaxReports /></MainLayout>} />
              <Route path="/financial-reports" element={<MainLayout><FinancialReports /></MainLayout>} />
              <Route path="/import-center" element={<MainLayout><ImportCenter /></MainLayout>} />
              <Route path="/prospects" element={<MainLayout><Prospects /></MainLayout>} />
              <Route path="/door-tracker" element={<MainLayout><DoorTracker /></MainLayout>} />
              <Route path="/autopilot" element={<MainLayout><FeatureGate requiredTier="professional"><Autopilot /></FeatureGate></MainLayout>} />
              <Route path="/ar-aging" element={<MainLayout><ARAging /></MainLayout>} />
              <Route path="/mileage" element={<MainLayout><MileageTracker /></MainLayout>} />
              <Route path="/form-1099" element={<MainLayout><Form1099 /></MainLayout>} />
              <Route path="/cash-flow-forecast" element={<MainLayout><CashFlowForecast /></MainLayout>} />
              <Route path="/crm" element={<MainLayout><CRM /></MainLayout>} />
              <Route path="/accounting-hub" element={<MainLayout><AccountingHub /></MainLayout>} />
              <Route path="/migrate" element={<MainLayout><MigrationWizard /></MainLayout>} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/features" element={<Features />} />
              <Route path="/about" element={<About />} />
              <Route path="/switch-and-save" element={<SwitchAndSave />} />
              <Route path="/migration-concierge" element={<MigrationConcierge />} />
              <Route path="/voice-ai" element={<MainLayout><VoiceAI /></MainLayout>} />
              <Route path="/security" element={<Security />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/status" element={<Status />} />
              <Route path="/admin/analytics" element={<MainLayout><AdminAnalytics /></MainLayout>} />
              <Route path="/admin/launch-readiness" element={<MainLayout><LaunchReadiness /></MainLayout>} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </SubscriptionProvider>
        </ThemeProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
